import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link2, Link2Off, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";

export default function XeroPanel() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/xero/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (status?.connected) api.get("/xero/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, [status?.connected]);
  useEffect(() => {
    const x = params.get("xero");
    if (!x) return;
    if (x === "connected") toast.success("Xero connected");
    else toast.error(`Xero connection failed (${params.get("reason") || "unknown"})`);
    params.delete("xero"); params.delete("reason"); setParams(params, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try { const r = await api.get("/xero/connect"); window.location.assign(r.data.url); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Xero connection"); setBusy(false); }
  };
  const disconnect = async () => { await api.post("/xero/disconnect"); toast.info("Xero disconnected"); load(); };
  const setBank = async (code) => {
    const a = accounts.find((x) => x.code === code);
    await api.put("/xero/settings", { bank_account_code: code, bank_account_name: a?.name || "" });
    toast.success(`Payments will be recorded to ${a?.name || code}`); load();
  };

  if (!status) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" />;
  if (!status.enabled) return <Card className="p-6 border-amber-200 bg-amber-50 text-sm text-amber-800" data-testid="xero-disabled">Xero credentials are not configured on the server.</Card>;

  return (
    <Card className="p-6 border-slate-200 space-y-5" data-testid="xero-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${status.connected ? "bg-emerald-100" : "bg-slate-100"}`}>
          {status.connected ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Link2Off className="w-6 h-6 text-slate-400" />}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-slate-900" data-testid="xero-status-text">{status.connected ? `Connected to ${status.organisation || "Xero"}` : "Xero not connected"}</p>
          <p className="text-xs text-slate-500">{status.connected ? "Every completed sale creates a customer contact and a PAID invoice in Xero automatically." : "Connect your Xero organisation to sync sales as paid invoices."}</p>
        </div>
        {status.connected ? (
          <Button variant="outline" size="sm" onClick={disconnect} className="rounded-full" data-testid="xero-disconnect-btn"><Link2Off className="w-4 h-4" /> Disconnect</Button>
        ) : (
          <Button onClick={connect} disabled={busy} className="rounded-full bg-[#13B5EA] hover:bg-[#0ea3d6] text-white" data-testid="xero-connect-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Connect Xero
          </Button>
        )}
      </div>
      {status.connected && (
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Payments recorded to</p>
            <Select value={status.bank_account_code || ""} onValueChange={setBank}>
              <SelectTrigger data-testid="xero-bank-select"><SelectValue placeholder="Choose bank account" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.name} ({a.code})</SelectItem>)}</SelectContent>
            </Select>
            {!status.bank_account_code && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Invoices will stay AUTHORISED (unpaid) until a bank account is chosen.</p>}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">GST treatment</p>
            <Badge variant="outline" className="border-slate-300">{status.tax_type === "OUTPUT" ? "GST on income (10%)" : status.tax_type === "BASEXCLUDED" ? "No GST (not registered)" : status.tax_type}{status.line_amount_types !== "NoTax" && ` · prices ${status.line_amount_types?.toLowerCase()}`}</Badge>
            <p className="text-xs text-slate-500 mt-1">{status.tax_type === "BASEXCLUDED" ? "Invoices are recorded without GST and excluded from BAS. Ask to switch this on once you register." : "Change via XERO_TAX_TYPE if you're not GST-registered."}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Invoice numbering</p>
            <Badge variant="outline" className="border-slate-300">WEB-&lt;order id&gt;</Badge>
            <p className="text-xs text-slate-500 mt-1">Reference shows Stripe/PayPal transaction ID.</p>
          </div>
        </div>
      )}
    </Card>
  );
}

export function XeroOrderCell({ order, onSynced }) {
  const [busy, setBusy] = useState(false);
  const x = order.xero;
  const retry = async () => {
    setBusy(true);
    try { const r = await api.post(`/xero/sync/${order.id}`); r.data.status === "synced" ? toast.success("Synced to Xero") : toast.error(r.data.error || "Sync failed"); onSynced(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };
  if (x?.status === "synced") return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`xero-synced-${order.id}`}>{x.invoice_number}</Badge>;
  return (
    <span className="inline-flex items-center gap-2">
      {x?.status === "error" && <span className="text-xs text-red-600 max-w-[160px] truncate" title={x.error}>Failed</span>}
      <Button size="sm" variant="ghost" disabled={busy} onClick={retry} className="h-7 px-2 text-xs" data-testid={`xero-sync-${order.id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync
      </Button>
    </span>
  );
}
